import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Box, Button, IconButton,
  Drawer, List, ListItem, ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-scroll';
import logo from '../assets/logo.png';
import { C, F, SH } from '../styles/tokens';
import { btnAmberSx } from '../styles/mixins';

const NAV_ITEMS = [
  { label: 'Services',     to: 'services' },
  { label: 'Our Clients',  to: 'clients' },
  { label: 'Testimonials', to: 'testimonials' },
  { label: 'Contact Us',   to: 'footer' },
];

const NAV_LINK_SX = {
  color:      C.textMid,
  fontWeight: 500,
  fontSize:   14.5,
  fontFamily: F.heading,
  px:         1.8,
  position:   'relative',
  background: 'transparent',
  '&::after': {
    content:         '""',
    position:        'absolute',
    bottom:          6,
    left:            '50%',
    transform:       'translateX(-50%)',
    width:           0,
    height:          2,
    backgroundColor: C.blue,
    borderRadius:    4,
    transition:      'width 0.25s ease',
  },
  '&:hover::after': { width: '60%' },
  '&:hover': { color: C.blue, background: 'transparent' },
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background:    'rgba(255,255,255,0.94)',
          backdropFilter: 'blur(14px)',
          borderBottom:  `1px solid ${C.border}`,
          boxShadow:     scrolled ? SH.card : 'none',
          transition:    'box-shadow 0.3s',
          color:         C.text,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, minHeight: 68 }}>
          <Link to="home" smooth duration={500} offset={-70} style={{ cursor: 'pointer' }}>
            <img src={logo} alt="Pracco" style={{ height: 38 }} />
          </Link>

          {/* Desktop nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {NAV_ITEMS.map(({ label, to }) => (
              <Link key={label} to={to} smooth duration={500} offset={-70}>
                <Button sx={NAV_LINK_SX}>{label}</Button>
              </Link>
            ))}
            <Link to="home" smooth duration={500} offset={-70}>
              <Button
                variant="contained"
                sx={{ ...btnAmberSx, ml: 1.5, fontSize: 13.5, px: 2.5, py: 1 }}
              >
                Book Free Consultation
              </Button>
            </Link>
          </Box>

          {/* Mobile hamburger */}
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'flex', md: 'none' }, color: C.text }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ width: 270, pt: 2, pb: 3, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <img src={logo} alt="Pracco" style={{ height: 32 }} />
            <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <CloseIcon />
            </IconButton>
          </Box>

          <List disablePadding>
            {NAV_ITEMS.map(({ label, to }) => (
              <ListItem key={label} disablePadding sx={{ mb: 0.5 }}>
                <Link
                  to={to}
                  smooth
                  duration={500}
                  offset={-70}
                  style={{ width: '100%', textDecoration: 'none' }}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontSize:   15,
                      fontWeight: 500,
                      color:      C.text,
                      fontFamily: F.heading,
                      py:         1,
                      px:         1.5,
                    }}
                  />
                </Link>
              </ListItem>
            ))}
          </List>

          <Box sx={{ mt: 3, px: 1 }}>
            <Link to="home" smooth duration={500} offset={-70} onClick={() => setMobileOpen(false)}>
              <Button fullWidth variant="contained" sx={{ ...btnAmberSx, py: 1.25, borderRadius: '8px' }}>
                Book Free Consultation
              </Button>
            </Link>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
